import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "../lib/AuthContext";
import { useDraftCampaign, useMyLiveCampaign } from "../lib/draftCampaign";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { RecurringGiftsCard } from "../components/RecurringGiftsCard";
import { CampaignBuilder } from "../components/builder/CampaignBuilder";
import { DashboardRejectionBanner } from "../components/DashboardRejectionBanner";
import { DashboardApprovalBanner } from "../components/DashboardApprovalBanner";
import { useNoIndex } from "../lib/useNoIndex";
import { DashboardPlate } from "../components/dashboard/DashboardPlate";
import { StudentSurveyCard } from "../components/survey/StudentSurveyCard";
import { PostSubmitSurvey } from "../components/survey/PostSubmitSurvey";

/**
 * Dashboard — students build, submit and maintain their one campaign here
 * (single-step: profile, funding need, Stripe payouts, media, attestations,
 * one submission, one review). Donors see their recurring gifts.
 * Header = DashboardPlate ("your file"); footer is the compact variant.
 */
export default function Dashboard() {
  useNoIndex();
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const isStudent = user?.role === "student";
  const draftState = useDraftCampaign({ auto: isStudent });
  const { live: liveCampaign, refresh: refreshLive } = useMyLiveCampaign({ auto: isStudent });
  const [submittedAt, setSubmittedAt] = useState(0);

  // Approval moment: the in-review poll flips the draft to live → fetch the live page for the banner.
  const draftStatus = draftState.draft?.status;
  useEffect(() => {
    if (isStudent && draftStatus === "live") refreshLive();
  }, [isStudent, draftStatus, refreshLive]);

  useEffect(() => {
    if (!loading && !user) navigate("/login", { replace: true, state: { from: "/dashboard" } });
  }, [loading, user, navigate]);

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  if (loading || !user) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-sand">
        <Loader2 className="h-6 w-6 animate-spin text-olive" />
      </main>
    );
  }

  const firstName = user.display_name?.split(" ")[0] || user.email?.split("@")[0];

  return (
    <main data-testid="dashboard-page" className="bg-sand">
      <Navbar onStart={handleLogout} primaryLabel="Sign out" secondaryLabel={null} />

      <DashboardPlate user={user} draft={draftState.draft} isStudent={isStudent} />

      <section className="mx-auto max-w-5xl px-6 pb-24 pt-14 lg:px-8 lg:pt-16">
        {isStudent && draftState.draft && (
          <div className="mb-10" data-testid="dashboard-rejection-slot">
            <DashboardRejectionBanner
              status={draftState.draft.status}
              rejectionReason={draftState.draft.rejection_reason}
              reviewedAt={draftState.draft.reviewed_at}
            />
          </div>
        )}
        {isStudent && liveCampaign && (
          <div className="mb-10" data-testid="dashboard-approval-slot">
            <DashboardApprovalBanner campaign={liveCampaign} studentFirstName={firstName} />
          </div>
        )}
        {isStudent && draftStatus && draftStatus !== "draft" && (
          <div className="mb-10" data-testid="dashboard-survey-slot">
            <StudentSurveyCard />
          </div>
        )}
        {isStudent && <PostSubmitSurvey trigger={submittedAt} />}

        {!isStudent && (
          <div data-testid="dashboard-recurring-slot">
            <RecurringGiftsCard compact />
          </div>
        )}

        {isStudent && (
          <div data-testid="dashboard-draft-slot">
            <CampaignBuilder draftState={draftState} onSubmitted={() => setSubmittedAt(Date.now())} />
          </div>
        )}
      </section>

      <Footer variant="minimal" />
    </main>
  );
}
